import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import parseErrorMessage from 'utils/parseErrorMessage';

interface IncidentMember {
  user_guid: string;
  incident_member_id?: string;
  [key: string]: any;
}

interface EditIncidentMemberParams {
  member: IncidentMember;
  incident_member_id: string;
}

interface MarkIncidentUserActiveParams {
  user_guid: string;
  incident_member_id: string;
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, { autoClose: false } as ToastOptions);
};

export const useEditIncidentMember = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ member }: EditIncidentMemberParams) => {
      const response = await axios(
        config.apiGateway.editIncidentMemberByAdmin,
        {
          method: 'POST',
          data: {
            incidentMember: member,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: ['incidentSchedule', incidentId],
      });
    },
    onError: (error: any) => {
      handleError(error, 'editIncidentMemberByAdminError');
    },
  });
};

export const useRemoveUserFromIncident = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: IncidentMember) => {
      const response = await axios(
        config.apiGateway.removeUserFromIncidentByAdmin,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid: user.user_guid,
            group_guid: groupGuid,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
      toast.success('User removed from incident successfully');
    },
    onError: (error: any) => {
      handleError(error, 'removeUserFromIncidentByAdminError');
    },
  });
};

export const useRemoveSelfFromIncident = (incidentId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: IncidentMember) => {
      const response = await axios(config.apiGateway.removeSelfFromIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid: user.user_guid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
      toast.success('Successfully left the incident');
    },
    onError: (error: any) => {
      handleError(error, 'removeSelfFromIncidentError');
    },
  });
};

export const useMarkIncidentUserActive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ user_guid }: MarkIncidentUserActiveParams) => {
      const response = await axios(config.apiGateway.markIncidentMemberActive, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          user_guid,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
      toast.success('User marked as active');
    },
    onError: (error: any) => {
      if (
        error.response?.data?.ERROR?.includes(
          'You can not work on two different incident at the same time.'
        )
      ) {
        toast.error(
          'User is already working on another incident. You can not work on two different incidents at the same time.',
          { autoClose: 10000 } as ToastOptions
        );
      } else {
        handleError(error, 'Failed to activate incident user');
      }
    },
  });
};

export const useMarkIncidentUserInactive = (
  incidentId: string,
  groupGuid: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      user_guid,
      logout_time,
      minutes_worked,
      incident_member_id,
    }: {
      user_guid: string;
      logout_time: Date;
      minutes_worked: number;
      incident_member_id: string;
    }) => {
      const response = await axios(
        config.apiGateway.markIncidentMemberDeactive,
        {
          method: 'POST',
          data: {
            incident_id: incidentId,
            user_guid,
            logout_time,
            minutes_worked,
            group_guid: groupGuid,
            incident_member_id,
          },
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update the roster data in the cache
      queryClient.setQueryData(
        ['incidentRoster', incidentId],
        (oldData: any) => {
          if (!oldData) return oldData;
          return oldData.map((member: any) => {
            if (member.incident_member_id === variables.incident_member_id) {
              return {
                ...member,
                active: 0,
                active_time: null,
              };
            }
            return member;
          });
        }
      );

      // Invalidate and refetch to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });

      toast.success('User marked as inactive');
    },
    onError: (error: any) => {
      handleError(error, 'Failed to deactivate incident user');
    },
  });
};
